import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { networkBus } from '@shared/network';
import { snakeizeKeys } from '../lib/snakeizeKeys';

// SSE идёт мимо интерцептора: события разбирают тем же разборщиком, что REST, пропустив через snakeizeKeys.
export { snakeizeKeys };

const API_HOST = import.meta.env.VITE_API_URL ?? '';
export const API_BASE_URL = `${API_HOST}/api/v1`;

/** Файл с сервера API: бэк отдаёт путь от корня, а `<audio>` идёт мимо axios. На проде приложение и API на разных
 *  доменах: без адреса API запрос уходит в приложение и получает его HTML (голос игры падал на браузерный, 2026-11-04). */
export const apiFileUrl = (path: string): string => (path.startsWith('/api/') ? `${API_HOST}${path}` : path);

export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 15_000,
});

function isServerUnreachable(err: AxiosError): boolean {
  if (err.code === 'ERR_CANCELED') return false;
  if (!err.response) return true;
  return err.response.status >= 500 && err.response.status <= 599;
}

api.interceptors.response.use(
  (response) => {
    response.data = snakeizeKeys(response.data);
    networkBus.serverOk();
    return response;
  },
  (error: AxiosError) => {
    if (isServerUnreachable(error)) {
      networkBus.serverError();
    } else {
      networkBus.serverOk();
    }
    return Promise.reject(error);
  },
);

type TokenGetter = () => string | null;
type TokenSetter = (token: string | null) => void;
type SignOutCallback = () => void;

let getAccessToken: TokenGetter = () => null;
let setAccessToken: TokenSetter = () => {};
let onUnauthorized: SignOutCallback = () => {};

export function configureAuth(opts: {
  getAccessToken: TokenGetter;
  setAccessToken: TokenSetter;
  onUnauthorized: SignOutCallback;
}) {
  getAccessToken = opts.getAccessToken;
  setAccessToken = opts.setAccessToken;
  onUnauthorized = opts.onUnauthorized;
}

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _skipAuth?: boolean;
}

api.interceptors.request.use((config) => {
  const cfg = config as RetryableConfig;
  if (cfg._skipAuth) return config;
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = api
      .post<{ access_token: string }>('/auth/refresh', undefined, {
        _skipAuth: true,
      } as RetryableConfig)
      .then(({ data }) => data.access_token)
      .catch(() => null)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetryableConfig | undefined;

    if (!original || error.response?.status !== 401 || original._retry) {
      return Promise.reject(error);
    }

    const url = original.url ?? '';
    if (
      url.includes('/auth/refresh') ||
      url.includes('/auth/login') ||
      url.includes('/auth/registration') ||
      url.includes('/auth/verify-user') ||
      url.includes('/auth/reset-password-confirmation')
    ) {
      return Promise.reject(error);
    }

    original._retry = true;
    const token = await refreshAccessToken();

    if (!token) {
      onUnauthorized();
      return Promise.reject(error);
    }

    setAccessToken(token);
    original.headers.Authorization = `Bearer ${token}`;
    return api(original);
  },
);
